import {
  LinedListSection as LinedListSectionUI,
  LinedListSectionProps as LinedListSectionUIProps,
} from "@repo/ui-parkmobile/sections/LinedListSection";
import type {
  LinedListSection,
  LinedListItem,
  LinedListItemDetailed,
  Accordion,
  Asset,
  Button,
  Multilink,
} from "@/generated/sb";
import {
  sectionHeaderResolver,
  buttonResolver,
  assetResolver,
  imageStoryblokResolver,
  multilinkResolver,
  textResolver,
} from "@/lib/storyblok/adaptors";
import { storyblokEditable } from "@storyblok/react/rsc";
import { useMemo } from "react";
import {
  trackCheckAvailabilityClick,
  trackFAQClick,
} from "@/utils/trackingScripts";

// Extended types for new fields (to be added in Storyblok schema)
type LinedListSectionExtended = LinedListSection & {
  image?: Asset;
};

type LinedListItemExtended = LinedListItem & {
  link?: Multilink;
  button?: Button[];
};

export type LinedListSectionProps = {
  blok: LinedListSectionExtended;
  pageType?: "default" | "location";
};

function resolveLinedListItem(item: LinedListItemExtended) {
  const { titleColor, priceColor, link, button, ...rest } = item;

  const resolvedLink = link ? multilinkResolver(link as any) : null;
  const resolvedButton = button?.[0] ? buttonResolver(button[0]) : null;

  return {
    ...rest,
    component: "linedListItem" as const,
    titleColor: (titleColor || "primary") as
      | "default"
      | "inverted"
      | "accent"
      | "faded"
      | "faded-dark",
    priceColor: (priceColor || "primary") as
      | "default"
      | "inverted"
      | "accent"
      | "faded"
      | "faded-dark",
    link: resolvedLink
      ? { href: String(resolvedLink.href), target: resolvedLink.target, noFollow: resolvedLink.noFollow }
      : undefined,
    button: resolvedButton || undefined,
  };
}

function resolveLinedListItemDetailed(
  item: LinedListItemDetailed,
  sectionName: string,
) {
  // Extract data for analytics
  const parkingName = item.title || "";
  const price = item.price || "";
  const address = item.subTitle || "";

  // Extract distance from features (e.g., "3 min walk")
  const distance =
    item.features?.find((f) => f.text?.includes("min"))?.text || "";

  const ctaText = item.button?.[0]?.children || "Check availability";

  // `image` is now a `bloks` field holding a single `image` component — the
  // shape every other card already uses (cardParking, cardLocations, …).
  const resolvedImage = imageStoryblokResolver(item.image?.[0]);
  return {
    ...item,
    component: "linedListItemDetailed" as const,
    image: resolvedImage || undefined,
    button: item.button
      ?.map(buttonResolver)
      .filter((btn): btn is NonNullable<typeof btn> => btn !== null),
    features: item.features?.map((feature) => {
      const resolvedIcon = feature.icon
        ? assetResolver(feature.icon)
        : undefined;
      return {
        ...feature,
        icon: resolvedIcon || undefined,
      };
    }),
    onButtonClick: () => {
      trackCheckAvailabilityClick({
        section: sectionName,
        config: "lineListSection",
        ctaText,
        parkingName,
        location: sectionName,
        price,
        distance,
        address,
        contentDescription: "",
      });
    },
  };
}

function resolveAccordion(item: Accordion, sectionName: string) {
  return {
    component: "accordion" as const,
    _uid: item._uid,
    items:
      item.items?.map((accordionItem) => ({
        title: accordionItem.title || "",
        text:
          accordionItem.text?.map((textBlok) => {
            // TODO: find how to override in storyblok
            // Hack: override default color from SB
            return { ...textResolver([{ ...textBlok, fontColor: "faded" }]) };
          }) || [],
        backgroundColor: accordionItem.backgroundColor || "none",
      })) || [],
    variant: (item.style === "secondary" ? "compact" : "default") as
      | "default"
      | "compact",
    onItemToggle: (index: number, title: string, isOpen: boolean) => {
      if (isOpen) {
        trackFAQClick({
          section: sectionName,
          config: "accordionItem",
          innerText: title,
        });
      }
    },
  };
}

export function LinedListSection({
  blok,
  pageType = "default",
  ...props
}: LinedListSectionProps) {
  const isLocationPage = pageType === "location";

  const rawTypographyProps = useMemo(
    () => sectionHeaderResolver(blok.typography),
    [blok.typography],
  );

  // TODO: Hardcoded fix for location pages - remove "by name" from section title
  const typographyProps = useMemo(() => {
    if (!rawTypographyProps || !isLocationPage) return rawTypographyProps;

    // Remove "by name" from title content
    if (rawTypographyProps.title?.content) {
      return {
        ...rawTypographyProps,
        title: {
          ...rawTypographyProps.title,
          content: rawTypographyProps.title.content
            .replace(/\s*by name\s*/gi, " ")
            .trim(),
        },
      };
    }
    return rawTypographyProps;
  }, [rawTypographyProps, isLocationPage]);

  const sectionName =
    typographyProps?.title?.content || "Parking Search Results";

  const resolvedItems = useMemo(() => {
    return (
      blok.items
        ?.map((item) => {
          switch (item.component) {
            case "linedListItem":
              return resolveLinedListItem(item as LinedListItemExtended);
            case "linedListItemDetailed":
              return resolveLinedListItemDetailed(
                item as LinedListItemDetailed,
                sectionName,
              );
            case "accordion":
              return resolveAccordion(item as Accordion, sectionName);
            default:
              return null;
          }
        })
        .filter((item): item is NonNullable<typeof item> => item !== null) || []
    );
  }, [blok.items, sectionName]);

  const resolvedImage = useMemo(() => {
    if (!blok.image) return undefined;
    return assetResolver(blok.image) || undefined;
  }, [blok.image]);

  const componentProps: LinedListSectionUIProps = {
    typography: typographyProps,
    items: resolvedItems,
    type: blok.type === "split" ? "split" : "default",
    image: resolvedImage,
    pageType,
    topPadding: blok.topPadding,
    bottomPadding: blok.bottomPadding,
  };

  return (
    <section
      {...storyblokEditable(blok)}
      className="uipm:px-[var(--container-margin)]"
      {...props}
    >
      <LinedListSectionUI {...componentProps} />
    </section>
  );
}
